import { Fingerprint, Key } from "lucide-react";
import * as React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui";
import { Badge, ByocBadge, ProBadge } from "@/components/ui/atoms";
import { cn } from "@/lib/utils";

export type SecretsSummary = { configured: string[]; pending: string[] };
export type OAuthProvider = {
  id: string;
  status: "active" | "inactive";
  scopes: string[];
};
export type OAuthSummary = { providers: OAuthProvider[] };

const SecretsBadgeImpl = ({
  secrets,
  className,
}: {
  secrets: SecretsSummary;
  className?: string;
}) => {
  const hasUnmetRequirements = (secrets.pending?.length ?? 0) > 0;

  const content = (
    <div className="space-y-3">
      <div className="font-medium text-gray-900 dark:text-gray-100">
        API Keys & Secrets
      </div>
      {(secrets.configured?.length ?? 0) > 0 && (
        <div>
          <div className="mb-1.5 font-medium text-emerald-700 text-xs dark:text-emerald-300">
            Configured
          </div>
          <ul className="list-disc space-y-1 pl-4 text-gray-700 text-xs dark:text-gray-300">
            {secrets.configured?.map((key) => (
              <li key={`cfg-${key}`}>{key}</li>
            ))}
          </ul>
        </div>
      )}
      {(secrets.pending?.length ?? 0) > 0 && (
        <div>
          <div className="mb-1.5 font-medium text-red-700 text-xs dark:text-red-300">
            Pending
          </div>
          <ul className="list-disc space-y-1 pl-4 text-gray-700 text-xs dark:text-gray-300">
            {secrets.pending?.map((key) => (
              <li key={`pen-${key}`}>{key}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            className={cn(
              "gap-1.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
              hasUnmetRequirements && "border-l-2 border-l-yellow-600",
              className
            )}
            variant="secondary"
          />
        }
      >
        <Key className="h-3.5 w-3.5" />
        Secrets
      </TooltipTrigger>
      <TooltipContent className="max-w-xs p-4 text-sm" sideOffset={12}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
};

const OAuthBadgeImpl = ({
  oauth,
  className,
}: {
  oauth: OAuthSummary;
  className?: string;
}) => {
  const active = oauth.providers.filter((p) => p.status === "active").length;
  const inactive = oauth.providers.length - active;
  const hasUnmetRequirements = inactive > 0;

  const content = (
    <div className="space-y-3">
      <div className="font-medium text-gray-900 dark:text-gray-100">
        OAuth Authorization
      </div>
      {oauth.providers.map((p) => (
        <div className="text-gray-700 text-xs dark:text-gray-300" key={p.id}>
          <span className="font-medium">{p.id}</span>{" "}
          {p.status === "active" ? (
            <span className="text-emerald-700 dark:text-emerald-300">
              (active)
            </span>
          ) : (
            <span className="text-red-700 dark:text-red-300">(inactive)</span>
          )}
          {p.scopes.length > 0 && (
            <div className="mt-1 pl-3 text-gray-600 dark:text-gray-400">
              {p.scopes.join(", ")}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            className={cn(
              "gap-1.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
              hasUnmetRequirements && "border-l-2 border-l-yellow-600",
              className
            )}
            variant="secondary"
          />
        }
      >
        <Fingerprint className="h-3.5 w-3.5" />
        OAuth
      </TooltipTrigger>
      <TooltipContent className="max-w-xs p-4 text-sm" sideOffset={12}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
};

function RequirementBadgesImpl({
  requirementsSummary,
  isByoc = false,
  isPro = false,
  badgeClassName,
}: {
  requirementsSummary: {
    secrets?: SecretsSummary;
    oauth?: OAuthSummary;
  };
  isByoc?: boolean;
  isPro?: boolean;
  badgeClassName?: string;
}) {
  if (
    !(
      requirementsSummary?.secrets ||
      requirementsSummary?.oauth ||
      isByoc ||
      isPro
    )
  ) {
    return null;
  }

  const { secrets, oauth } = requirementsSummary;

  return (
    <div className="flex w-full flex-wrap items-center gap-1.5">
      {secrets && <SecretsBadge className={badgeClassName} secrets={secrets} />}
      {oauth && <OAuthBadge className={badgeClassName} oauth={oauth} />}
      {isByoc && (
        <ByocBadge className={cn("gap-1.5 rounded-md", badgeClassName)} />
      )}
      {isPro && (
        <ProBadge className={cn("gap-1.5 rounded-md", badgeClassName)} />
      )}
    </div>
  );
}

import {
  areStringArraysEqual,
  createPropsComparator,
} from "@/components/ui/utils/memo";

const SecretsBadge = React.memo(SecretsBadgeImpl, (prev, next) => {
  if (prev.className !== next.className) {
    return false;
  }
  if (prev.secrets === next.secrets) {
    return true;
  }
  if (!(prev.secrets && next.secrets)) {
    return false;
  }
  const configuredEqual = areStringArraysEqual(
    prev.secrets.configured,
    next.secrets.configured
  );
  if (!configuredEqual) {
    return false;
  }
  const pendingEqual = areStringArraysEqual(
    prev.secrets.pending,
    next.secrets.pending
  );
  if (!pendingEqual) {
    return false;
  }
  return true;
});

const OAuthBadge = React.memo(OAuthBadgeImpl, (prev, next) => {
  if (prev.className !== next.className) {
    return false;
  }
  if (prev.oauth === next.oauth) {
    return true;
  }
  if (!(prev.oauth && next.oauth)) {
    return false;
  }
  const a = prev.oauth.providers;
  const b = next.oauth.providers;
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const pa = a[i];
    const pb = b[i];
    const sameIdAndStatus = pa.id === pb.id && pa.status === pb.status;
    if (!sameIdAndStatus) {
      return false;
    }
    if (!areStringArraysEqual(pa.scopes, pb.scopes)) {
      return false;
    }
  }
  return true;
});

const RequirementBadges = React.memo(
  RequirementBadgesImpl,
  createPropsComparator<{
    requirementsSummary: {
      secrets?: SecretsSummary;
      oauth?: OAuthSummary;
    };
    isByoc?: boolean;
    isPro?: boolean;
    badgeClassName?: string;
  }>(["requirementsSummary", "isByoc", "isPro", "badgeClassName"])
);

export { OAuthBadge, RequirementBadges, SecretsBadge };
